import { CurrencyPipe } from '@angular/common';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { MAT_SELECT_SCROLL_STRATEGY_PROVIDER } from '@angular/material/select';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MAT_TOOLTIP_SCROLL_STRATEGY_FACTORY_PROVIDER } from '@angular/material/tooltip';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, type Routes, withPreloading, withRouterConfig } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { IntercomModule } from '@supy-io/ngx-intercom';

import { APP_CONTEXT_ROUTES } from '@efficiently/client-api-app-context';
import { ELEMENTS_CONFIG, ElementsConverter, SettingsStructure } from '@efficiently/client-api-application-settings';
import { ThemeScreenshotService } from '@efficiently/client-api-book-themes';
import { SchedulesRouteBuilder } from '@efficiently/client-api-schedules';
import { provideEffLayers } from '@efficiently/client-cdk-layer';
import { CDK_OVERLAY_CONTAINER_PROVIDER, DYNAMIC_OVERLAY_PROVIDERS } from '@efficiently/client-cdk-overlays';
import {
	duplicateRequestHttpInterceptor,
	provideDuplicateRequestMonitoring,
} from '@efficiently/client-core-duplicate-request-monitoring';
import { environment } from '@efficiently/client-core-environment';
import { GlobalErrorHandlerService as LibGlobalErrorHandlerService } from '@efficiently/client-core-error';
import { clientLoggerHttpInterceptor, provideClientLogger } from '@efficiently/client-core-logger';
import { isClientSentryEnabled } from '@efficiently/client-core-observability';
import { APP_ROUTES, PAGES_ROUTES } from '@efficiently/client-core-routing';
import { provideClientLogsDataAccess } from '@efficiently/client-data-access-client-logs';
import { provideHistoryDataAccess } from '@efficiently/client-data-access-history';
import { provideOrgDataAccess } from '@efficiently/client-data-access-org';
import { providePlansDataAccess } from '@efficiently/client-data-access-plans';
import { provideLocationsDataAccess } from '@efficiently/client-data-access-project-locations';
import { provideRecentsDataAccess } from '@efficiently/client-data-access-recents';
import { accountsProviders } from '@efficiently/client-domain-accounts';
import { appContextProviders } from '@efficiently/client-domain-app-context';
import { approvalsProviders } from '@efficiently/client-domain-approvals';
import { provideAuth, provideAuthLoginPath } from '@efficiently/client-domain-auth';
import { bookThemesProviders } from '@efficiently/client-domain-book-themes';
import { chatProviders } from '@efficiently/client-domain-chat';
import { constructionsProviders } from '@efficiently/client-domain-constructions';
import { finishProviders } from '@efficiently/client-domain-finish';
import { finishIdProviders } from '@efficiently/client-domain-finish-id';
import { finishTemplateProviders } from '@efficiently/client-domain-finish-template';
import { inviteProviders } from '@efficiently/client-domain-invite';
import { itemCatalogProviders } from '@efficiently/client-domain-item-catalog';
import { orgAdminProviders } from '@efficiently/client-domain-org-admin';
import { provideCurrentOrg } from '@efficiently/client-domain-org-context';
import { permissionsProviders } from '@efficiently/client-domain-permissions';
import { providePortalAuth } from '@efficiently/client-domain-portal-auth';
import { portalConfigProviders } from '@efficiently/client-domain-portal-config';
import { provideRoomsDataAccess } from '@efficiently/client-domain-project-rooms';
import { projectsProviders } from '@efficiently/client-domain-projects';
import { provisioningProviders } from '@efficiently/client-domain-provisioning';
import { stakeholdersProviders } from '@efficiently/client-domain-stakeholders';
import { tagsProviders } from '@efficiently/client-domain-tags';
import { usersProviders } from '@efficiently/client-domain-users';
import { EnumDelimiterPipe } from '@efficiently/client-ui-common';
import { EFF_ICON_SPRITE_PATH } from '@efficiently/client-ui-icons';
import { provideEffSpinner } from '@efficiently/client-ui-kit';
import { SPINNER_PROVIDERS } from '@efficiently/client-widget-loader';

import { GlobalErrorHandlerService } from '@ds/core/services/global-error-handler.service';
import { BookPageScreenshotService } from '@ds/pages/projects/components/project-detail/tabs/books-konva/book/book-shared/book-screenshot/book-page-screenshot.service';
import { ApplicationSettingsContainerService } from '@ds/shared/modules/application-settings/application-settings-container.service';
import elementsJson from '@ds/shared/modules/application-settings/settings-test.json';

import { APP_DOMAIN_PROVIDERS } from './app.providers';
import { appGraphqlProviders } from './app-graphql.providers';
import { appStoreProviders } from './app-store.providers';
import { provideTypedDateAdapter } from './core/date/typed-date.providers';
import { iconSpritePath } from './core/icons/icon-sprite-path.util';
import { AppInitService } from './core/services/app-init.service';
import { AppPreloadingStrategy } from './core/services/app-preloading-strategy.service';
import { resolveLoginPath } from './core/services/login-path.resolver';
import { ROUTER_CONFIG_WRAPPER } from './core/services/router-config-wrapper.provider';
import { DesignSchedulesRouteBuilder } from './core/services/schedules-route-builder/design-schedules-route-builder.service';
import { UnloadSyncService } from './core/services/unload-sync.service';

/**
 * The application's providers, over the route table it is given.
 *
 * A function rather than a constant because the route table is the ONLY thing that differs between
 * the two builds this application produces, and each has its own entry point: `main.ts` passes the
 * Design table, `main.clarity.ts` the one that carries the Control Center. Importing a table here
 * instead would put both shells — and through them the whole platform chrome and Clarity — into
 * every bundle, since `imports` and a module-level import are resolved statically whether or not
 * anything reaches the code behind them.
 */
export function createAppConfig(routes: Routes): ApplicationConfig {
	return {
		providers: [
			{ provide: EFF_ICON_SPRITE_PATH, useValue: iconSpritePath(environment.build) },
			importProvidersFrom(
				IntercomModule.forRoot({
					appId: '',
					updateOnRouterChange: true,
				}),
			),
			...provideTypedDateAdapter(),
			provideAnimations(),
			provideHttpClient(
				withInterceptorsFromDi(),
				withInterceptors([duplicateRequestHttpInterceptor, clientLoggerHttpInterceptor]),
			),
			...provideDuplicateRequestMonitoring({
				app: 'design-schedule',
				environmentName: environment.name,
				production: environment.production,
				sentryEnabled: isClientSentryEnabled({ environment }),
			}),
			provideClientLogger({
				app: 'design-schedule',
				env: environment.name,
			}),
			provideClientLogsDataAccess(),
			provideRouter(
				routes,
				withPreloading(AppPreloadingStrategy),
				withRouterConfig({
					onSameUrlNavigation: 'reload',
				}),
			),
			...appStoreProviders,
			{
				provide: Sentry.TraceService,
				deps: [Router],
			},
			AppInitService,
			CDK_OVERLAY_CONTAINER_PROVIDER,
			DYNAMIC_OVERLAY_PROVIDERS,
			MAT_TOOLTIP_SCROLL_STRATEGY_FACTORY_PROVIDER,
			MAT_SELECT_SCROLL_STRATEGY_PROVIDER,
			provideAppInitializer(() => {
				// TraceService and UnloadSyncService are injected to ensure their construction;
				// startApp drives the actual initialization.
				inject(Sentry.TraceService);
				inject(UnloadSyncService);
				return inject(AppInitService).startApp();
			}),
			ROUTER_CONFIG_WRAPPER,
			{
				provide: Window,
				useValue: window,
			},
			{
				provide: ErrorHandler,
				useExisting: GlobalErrorHandlerService,
			},
			{
				provide: MAT_TABS_CONFIG,
				useValue: { animationDuration: '0ms' },
			},
			{
				provide: MatSnackBarRef,
				useValue: {},
			},
			{
				provide: MAT_SNACK_BAR_DATA,
				useValue: {},
			},
			{
				provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
				useValue: {
					showDelay: 100,
					hideDelay: 100,
					touchendHideDelay: 100,
					position: 'above',
				},
			},
			CurrencyPipe,
			EnumDelimiterPipe,
			APP_DOMAIN_PROVIDERS,
			...provideEffSpinner(),
			provideEffLayers(),
			...SPINNER_PROVIDERS,
			...accountsProviders,
			...orgAdminProviders,
			...bookThemesProviders,
			...chatProviders,
			...appContextProviders,
			...permissionsProviders,
			provideAuth(),
			...usersProviders,
			...inviteProviders,
			...stakeholdersProviders,
			...provisioningProviders,
			provideOrgDataAccess(),
			...finishProviders,
			...finishIdProviders,
			...finishTemplateProviders,
			...itemCatalogProviders,
			...approvalsProviders,
			providePortalAuth(),
			...portalConfigProviders,
			...projectsProviders,
			provideRoomsDataAccess(),
			...constructionsProviders,
			...tagsProviders,
			provideHistoryDataAccess(),
			providePlansDataAccess(),
			provideLocationsDataAccess(),
			provideRecentsDataAccess(),
			// TODO(EFF-21373): Temporary DI bridge for the application-settings feature migration.
			//
			// Why this lives in app.config (not in domain-application-settings's `*Providers`):
			//   The legacy `ApplicationSettingsContainerService` lives in `packages/client/design-schedule`
			//   (the `@efficiently/client` app). Placing this binding inside `domain-application-settings`
			//   would create a circular project-level dependency (`domain-application-settings` →
			//   `@efficiently/client` via `@ds/...` import, while bridges in the app re-export from
			//   `domain-application-settings` back). Hosting the bridge in the app keeps the lib clean
			//   (depends only on `api-application-settings`) and confines the temporary coupling to
			//   the app boot.
			//
			// Why `useExisting` (not `useClass`):
			//   The legacy class also owns UI orchestration (MatDialog/Snackbar/Confirm) consumed by the
			//   container component and the form-builder. Splitting just the three pure converters off
			//   would cut a single file in half. The abstraction is wired to the same singleton so we
			//   keep the legacy class intact.
			//
			// When/how to remove:
			//   1. When the application-settings UI components migrate into `widget-application-settings`,
			//      add `DefaultElementsConverter` inside `domain-application-settings/src/lib/services/`.
			//   2. Re-introduce `applicationSettingsProviders` in that lib bound via `useClass`.
			//   3. Replace this binding with `...applicationSettingsProviders`.
			//   4. Drop `extends ElementsConverter` from `ApplicationSettingsContainerService`.
			{ provide: ElementsConverter, useExisting: ApplicationSettingsContainerService },
			// TODO(EFF-21373): Temporary DI bridge for the application-settings JSON config.
			//
			// Why this lives here:
			//   The JSON file `settings-test.json` is kept at its legacy path
			//   (`packages/client/design-schedule/src/app/shared/modules/application-settings/settings-test.json`)
			//   to preserve git history and to keep the legacy consumer's import line
			//   byte-identical with HEAD. Importing the JSON inside `domain-application-settings`
			//   would require either an `@ds/...` import (forbidden in a lib) or duplicating
			//   the file. The DI token decouples the lib from the JSON's physical location.
			//
			// When/how to remove:
			//   When the application-settings UI migrates into `widget-application-settings`
			//   (Step 5), relocate `settings-test.json` next to its new owner (api or
			//   domain lib of the new feature) and `import elementsJson from '...'` directly
			//   inside that lib. The `ELEMENTS_CONFIG` token and this `useValue` binding
			//   become unnecessary and should be deleted.
			{ provide: ELEMENTS_CONFIG, useValue: elementsJson as unknown as SettingsStructure },
			provideCurrentOrg(),
			provideAuthLoginPath(resolveLoginPath),
			{ provide: LibGlobalErrorHandlerService, useExisting: GlobalErrorHandlerService },
			{ provide: ThemeScreenshotService, useExisting: BookPageScreenshotService },
			// The root binding, so every reader of the schedule's route contract gets the Design URLs.
			// A host that mounts the schedule elsewhere overrides this class on its own route.
			{ provide: SchedulesRouteBuilder, useClass: DesignSchedulesRouteBuilder },
			...appGraphqlProviders,
			{
				provide: APP_CONTEXT_ROUTES,
				useValue: {
					projectsPrefix: `/${PAGES_ROUTES.PROJECTS.path}`,
					scopePrefix: `/${APP_ROUTES.WEB_HOSTED_SCOPE.path}`,
					scopeLoginPrefix: `/${APP_ROUTES.REQUEST_ACCESS_SCOPE.path}`,
					acceptInvitePath: PAGES_ROUTES.ACCEPT_INVITE.path,
					skipUrlPrefixes: [`/${APP_ROUTES.CLIENT_APP_PORTAL.path}`],
					supplyProjectPrefix: `/${APP_ROUTES.SUPPLY.path}/${APP_ROUTES.SUPPLY_PROJECT.path}`,
				},
			},
		],
	};
}
